<script lang="ts">
  import { fade } from "svelte/transition";
  import { _ } from "svelte-i18n";
  import { goto } from "$app/navigation";
  import Typography from "$lib/components/Typography/Typography.svelte";
  import { linkTo } from "$lib/utils/url";
  import Carousel from "../Carousel/Carousel.svelte";
  import { CarouselSize } from "$lib/gql/genCms/codegen";
  import SearchProductsList from "../SearchProductsList/SearchProductsList.svelte";

  export let open = false;
  export let querySuggestions: {
    text: string;
    display: string;
    count: string;
  }[] = [];
  export let productSuggestions;

  export let closeSearch: () => void;

  export let redirect: string;
  export let searchTerm;

  let viewportWidth: number;
  let isMobile = false;

  let lgWidth = 1280;

  $: isMobile = viewportWidth <= lgWidth;

  $: if (isMobile) {
    open = true;
  }

  const handleSearch = (selectedQuery: string) => {
    if (selectedQuery) {
      goto(linkTo(`search?term=${selectedQuery}`));
      closeSearch();
    }
  };

  function handleRedirect() {
    if (redirect) {
      closeSearch();
      goto(redirect);
    }
  }
</script>

<style lang="scss">
  .search-banner {
    display: flex;
    flex-direction: row;
    position: absolute;
    width: 100%;
    left: 0;
    max-width: $container-max;
    left: 50%;
    transform: translate(-50%, 0);
    &--mobile {
      display: flex;
      flex-direction: column;
      width: 100%;
      max-height: 60vh;
      padding: 0 0rem;
      justify-content: space-between;
      overflow: scroll;
      @media (min-width: $md) {
        padding: 0 2.75rem;
      }
    }

    &--wrapper {
      border-top: 0.5px solid $background;
      display: flex;
      padding-left: 2rem;
      padding-right: 2rem;
      background-color: $surface;

      width: calc(100% + 4rem);
      margin-left: -2rem;
      margin-right: -2rem;

      @include breakpoint(md) {
        width: calc(100% + 5.5rem);
        margin-left: -2.75rem;
        margin-right: -2.75rem;

        padding-left: 2.75rem;
        padding-right: 2.75rem;
      }

      @include breakpoint(lg) {
        width: calc(100% + 6.5rem);
        margin-left: -3.25rem;
        margin-right: -3.25rem;

        padding-left: 3.25rem;
        padding-right: 3.25rem;
      }

      @include breakpoint(xl) {
        width: calc(100% + 8rem);
        margin-left: -4rem;
        margin-right: -4rem;

        padding-left: 4rem;
        padding-right: 4rem;
      }

      @include breakpoint(xxl) {
        width: calc(100% + 12rem);
        margin-left: -6rem;
        margin-right: -6rem;

        padding-left: 6rem;
        padding-right: 6rem;
      }
    }

    &--options-queries {
      position: relative;
      width: 25%;
      background-color: $surface;
      z-index: 10;
      border-right: 0.5px solid $background;
      border-top: none;
      padding-top: 1rem;
      padding-bottom: 1rem;

      overflow: auto;
      left: 0;
    }

    &--options-queries-mobile {
      width: 100%;
      background-color: $surface;
      z-index: 10;
      border-bottom: 1px solid $background;
      border-top: none;
      overflow: visible;
      padding: 0 2rem;
    }

    &--options-products {
      position: relative;
      width: 100%;
      background-color: $surface;
      z-index: 10;
      border-left: 0.5px solid $background;
      border-right: 0.5px solid $background;
      align-items: center;
      border-top: none;

      overflow: auto;
      left: 0;
      justify-content: space-evenly;
    }

    &--grid {
      width: 100%;
      &--mobile {
        padding: 2rem 0;
        width: 100%;
      }
    }

    &--options-products-mobile {
      position: relative;
      width: 100%;
      height: 100%;
      background-color: $surface;
      z-index: 10;
      border-top: none;
      align-items: normal;
      overflow-y: visible;

      padding: 0 2rem;
    }

    &--button {
      background-color: transparent;
      border: none;
      color: $on-background;
      padding: 0;
      max-width: 10%;

      &:disabled {
        color: $on-surface-30;
      }

      @include breakpoint(lg) {
        &:hover {
          color: $on-surface-30;
        }
      }
    }

    &--redirect {
      padding: 1rem 2rem;
      cursor: pointer;
    }
  }
  .query {
    &--item {
      display: flex;
      padding: 13px 13px 13px 0px;
      cursor: pointer;
      text-transform: capitalize;
      justify-content: space-between;
      text-decoration: none;
      z-index: 50;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  .product {
    &--item {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      align-items: center;

      padding: 0px 16px;
      cursor: pointer;
      text-transform: capitalize;
    }
  }

  .loading-wrapper {
    background-color: $background;

    height: 100%;
    display: flex;
    align-items: center;
    width: 100%;
    position: fixed;
  }

  :global([class^="search-banner"] .product-card--info) {
    border-bottom: 0 !important;
  }

  :global([class^="search-banner"] .carousel--cards) {
    padding-bottom: 0 !important;
  }

  :global(.search-banner .carousel--header) {
    padding-top: 1.5rem !important;
    padding-bottom: 1.5rem !important;
    justify-content: flex-end !important;
  }

  :global([class^="search-banner no-controls"] .carousel--header) {
    display: none;
  }

  :global(#query-title > strong) {
    font-size: 13px;
    line-height: 19.2px;
  }
  :global(#query-title) {
    font-size: 13px;
    line-height: 19.2px;
  }
  :global(.search-banner--redirect > strong) {
    font-size: 13px;
    line-height: 15.6px;
  }
  :global(.search-banner--redirect) {
    font-size: 13px;
    line-height: 15.6px;
    text-transform: uppercase;
  }
</style>

<svelte:window bind:innerWidth={viewportWidth} />

{#if open}
  <!-- svelte-ignore a11y-no-static-element-interactions -->

  {#if isMobile}
    <div
      class={`search-banner--mobile`}
      on:mouseleave
      on:mouseenter
      transition:fade={{ duration: 300 }}
    >
      <div
        class={`search-banner--options-queries-mobile`}
        transition:fade={{ duration: 400 }}
      >
        {#each querySuggestions as query}
          <div
            class="query--item"
            on:click|preventDefault={() =>
              handleSearch(encodeURIComponent(query.text))}
            on:mousedown|preventDefault={() =>
              handleSearch(encodeURIComponent(query.text))}
            on:keydown|preventDefault={() =>
              handleSearch(encodeURIComponent(query.text))}
          >
            <Typography
              variant="body2"
              component="span"
              theme="on-background"
              id="query-title"
            >
              {@html query.display}
            </Typography>
            <Typography
              variant="button1"
              component="span"
              theme="on-background"
            >
              {parseInt(query.count) === 100 ? "99+" : query.count}
            </Typography>
          </div>
        {/each}
      </div>

      <div
        class={`search-banner--options-products-mobile`}
        transition:fade={{ duration: 400 }}
      >
        <SearchProductsList
          cards={productSuggestions}
          key="search"
          size={CarouselSize.Four}
          quickBuy={true}
          total={productSuggestions?.totalCount}
          {closeSearch}
        />
      </div>
      {#if redirect}
        <span
          transition:fade={{ duration: 400 }}
          class="search-banner--redirect"
          on:click={handleRedirect}
          on:keydown={handleRedirect}
          >{$_("Read More")}: <strong>{searchTerm}</strong>
        </span>
      {/if}
    </div>
  {:else}
    <div
      class={`search-banner ${
        productSuggestions?.totalCount <= 4 && "no-controls"
      }`}
      on:mouseleave
      on:mouseenter
      transition:fade={{ duration: 300 }}
    >
      <div class={"search-banner--wrapper"}>
        <!-- svelte-ignore a11y-no-static-element-interactions -->
        <div
          class={`search-banner--options-queries`}
          transition:fade={{ duration: 300 }}
        >
          {#if querySuggestions?.length > 0}
            {#each querySuggestions as query}
              <div
                class="query--item"
                on:click|preventDefault={() =>
                  handleSearch(encodeURIComponent(query.text))}
                on:mousedown={() =>
                  handleSearch(encodeURIComponent(query.text))}
                on:keydown={() => handleSearch(encodeURIComponent(query.text))}
              >
                <Typography
                  component="span"
                  theme="on-background"
                  id="query-title"
                >
                  {@html query.display}
                </Typography>
                <Typography component="span" theme="on-background">
                  {parseInt(query.count) === 100 ? "99+" : query.count}
                </Typography>
              </div>
            {/each}
          {/if}

          {#if redirect && searchTerm}
            <span
              transition:fade={{ duration: 300 }}
              class="search-banner--redirect"
              on:click={handleRedirect}
              on:keydown={handleRedirect}
            >
              <Typography
                component="span"
                theme="on-background"
                id="search-redirect"
              >
                {$_("Read More")}: <strong>{searchTerm}</strong></Typography
              >
            </span>
          {/if}
        </div>

        <div
          class={`search-banner--options-products`}
          transition:fade={{ duration: 300 }}
        >
          <Carousel
            cards={productSuggestions}
            id="search"
            controls={!isMobile}
            key="search"
            size={CarouselSize.Four}
            quickBuy={true}
            withPadding={false}
            total={productSuggestions?.totalCount}
            {closeSearch}
          />
        </div>
      </div>
    </div>
  {/if}
{/if}
